<script setup lang="ts">
import { computed, provide } from 'vue';
import RecordChips from '@/components/record/RecordChips.vue';
import { buildEntityColumns } from '@/composables/entityColumns';
import { inspectorComponentFor, inspectorExcludeFieldsFor } from '@/composables/searchInspectorRegistry';
import { RECORD_PAGE_CONTEXT } from '@/composables/recordPageContext';
import { PEEK_ORIGIN_TITLE } from '@/composables/peekOrigin';
import { applyLayout, chipLabelsOf, type RecordLayoutDefinition } from '@/composables/recordLayout/recordLayoutDefinition';
import { recordHeadingText } from '@/composables/recordLayout/recordHeading';
import { recordNounFor } from '@/composables/recordLayout/useRecordHeading';

/**
 * A record's body, the same on its own page and in the side peek: what it is
 * and where it sits, its name, the pinned field chips, and its sections, all
 * drawn from the person's layout. The page and the peek wrap it in their own
 * header.
 *
 * The top line and the title come from the layout, {@see recordHeadingText}.
 * In the side peek and the list's pane, the host's header draws
 * the same two rows from the same layout, so the compact body starts at the
 * chips rather than naming the record twice.
 */
const props = withDefaults(
    defineProps<{
        entityId: string;
        recordId: number | string;
        row: Record<string, unknown>;
        layout: RecordLayoutDefinition;
        initialSection?: string | null;
        /** The side peek's narrower, smaller version. */
        compact?: boolean;
        /** The page keeps the open tab in its address. The peek does not. */
        onSectionChange?: (section: string) => void;
    }>(),
    { initialSection: null, compact: false, onSectionChange: undefined },
);

const columns = computed(() => buildEntityColumns(props.entityId));
const detailComponent = computed(() => inspectorComponentFor(props.entityId));
const excludeFields = computed(() => inspectorExcludeFieldsFor(props.entityId));
const noun = computed(() => recordNounFor(props.entityId));

// The top line and the title, from the layout. The peeks draw the same words in their own header.
const heading = computed(() => recordHeadingText(props.layout, columns.value, props.row, noun.value, props.recordId));
const headerColumns = computed(() => applyLayout(props.layout, columns.value, { entityId: props.entityId }).headerColumns);
const hideLabels = computed(() => chipLabelsOf(props.layout) === 'hide');

// A related link in this body opens a peek whose trail starts with this record's name.
provide(PEEK_ORIGIN_TITLE, computed(() => heading.value.title));

provide(RECORD_PAGE_CONTEXT, {
    onSectionChange: (section: string) => props.onSectionChange?.(section),
    groupsFor: (pageColumns, pageExcludeFields) => applyLayout(props.layout, pageColumns, { excludeFields: pageExcludeFields, entityId: props.entityId }).groups,
});
</script>

<template>
  <div class="flex flex-col" :class="compact ? 'gap-4' : 'gap-5'" data-testid="record-body">
    <div v-if="!compact" class="flex flex-col gap-1.5">
      <p v-if="heading.topLine" class="text-sm text-muted-foreground" data-testid="record-place-line">{{ heading.topLine }}</p>
      <h1 class="text-3xl font-semibold tracking-tight" data-testid="record-title">{{ heading.title }}</h1>
    </div>

    <RecordChips :columns="headerColumns" :row="row" :hide-labels="hideLabels" />

    <div class="@container">
      <component
        :is="detailComponent"
        :row="row"
        :columns="columns"
        :exclude-fields="excludeFields"
        :entity-id="entityId"
        :initial-section="initialSection"
      />
    </div>
  </div>
</template>
